import { useParams, Link, Navigate } from 'react-router-dom';
import { TABS_BY_SLUG } from '../tabs/tabs-data';
import { TabHeader } from '../components/TabHeader';
import { TabSearchStrip } from '../components/TabSearchStrip';
import { TabDiagram, hasDiagram } from '../components/TabDiagrams';
import { Footer } from '../components/Footer';

/**
 * Generic tab detail page renderer. Every route in TABS pipes through this
 * component; the content shape is defined in tabs-data.ts per PRD §6.
 * Unknown slugs fall through to the home page (belt-and-braces — App.tsx
 * only wires known slugs so this branch is defensive).
 */
export function TabPage(): JSX.Element {
  const { slug = '' } = useParams();
  const tab = TABS_BY_SLUG[slug];
  if (!tab) return <Navigate to="/" replace />;

  const populated = tab.populated;

  return (
    <div className="flex min-h-screen flex-col bg-sovereign-900">
      <TabHeader />

      <main className="flex-1 px-4 py-10 md:px-8 md:py-14">
        <article className="mx-auto max-w-3xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-ink-dim">
            <Link to="/" className="hover:text-brand-purple hover:underline">
              Home
            </Link>
            <span className="mx-2" aria-hidden>·</span>
            <span>{tab.title}</span>
          </nav>

          <header className="mb-8 border-b border-sovereign-line pb-6">
            <div className="mb-3 flex items-center gap-3">
              <span
                className="grid h-10 w-10 place-items-center rounded-md bg-brand-purple text-xl text-white shadow-card"
                aria-hidden
              >
                {tab.glyph}
              </span>
              <h1 className="text-2xl font-bold text-ink md:text-3xl">{tab.title}</h1>
            </div>
            <p className="text-lg leading-relaxed text-ink-mute">{tab.promise}</p>
          </header>

          {populated ? (
            <>
              <section className="mb-6 text-[15px] leading-relaxed text-ink">
                <p>{tab.paragraphs[0]}</p>
              </section>

              {hasDiagram(tab.slug) && <TabDiagram slug={tab.slug} />}

              {tab.paragraphs.length > 1 && (
                <section className="mb-10 space-y-4 text-[15px] leading-relaxed text-ink">
                  {tab.paragraphs.slice(1).map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </section>
              )}

              {tab.howItWorks.length > 0 && (
                <section aria-labelledby="how-it-works" className="mb-10">
                  <h2
                    id="how-it-works"
                    className="mb-4 text-sm font-bold uppercase tracking-wide text-brand-purple"
                  >
                    How it works
                  </h2>
                  <ol className="space-y-4">
                    {tab.howItWorks.map((step, i) => (
                      <li
                        key={i}
                        className="rounded-md border border-sovereign-line bg-white p-4 shadow-card"
                      >
                        <div className="mb-1 text-sm font-bold text-ink">{step.step}</div>
                        <div className="text-sm leading-relaxed text-ink-mute">
                          {step.description}
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {tab.evidence.length > 0 && (
                <section aria-labelledby="evidence" className="mb-10">
                  <h2
                    id="evidence"
                    className="mb-4 text-sm font-bold uppercase tracking-wide text-brand-purple"
                  >
                    Evidence
                  </h2>
                  <ul className="space-y-2 text-[15px] text-ink">
                    {tab.evidence.map((e, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="mt-1 text-brand-purple" aria-hidden>▸</span>
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {tab.downloads && tab.downloads.length > 0 && (
                <section aria-labelledby="downloads" className="mb-10">
                  <h2
                    id="downloads"
                    className="mb-4 text-sm font-bold uppercase tracking-wide text-brand-purple"
                  >
                    Download
                  </h2>
                  <ul className="space-y-2">
                    {tab.downloads.map((d) => (
                      <li
                        key={d.label}
                        className="rounded-md border border-sovereign-line bg-white p-3 text-sm text-ink shadow-card"
                      >
                        <div className="font-semibold">{d.label}</div>
                        <div className="text-xs text-ink-mute">{d.note}</div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          ) : (
            <section className="mb-10 rounded-md border border-dashed border-sovereign-line bg-white p-6 text-center text-sm text-ink-mute">
              <p className="font-semibold text-ink">Full content lands in Sprint 2.</p>
              <p className="mt-2">
                {tab.paragraphs[0] ??
                  `The ${tab.title} tab will populate with the same skeleton (promise → paragraphs → how it works → evidence) as the anchor tabs.`}
              </p>
            </section>
          )}

          {tab.continueSlugs.length > 0 && (
            <section aria-labelledby="continue" className="mb-4">
              <h2
                id="continue"
                className="mb-3 text-sm font-bold uppercase tracking-wide text-brand-purple"
              >
                Continue
              </h2>
              <div className="flex flex-wrap gap-2">
                {tab.continueSlugs.map((s) => {
                  const t = TABS_BY_SLUG[s];
                  if (!t) return null;
                  return (
                    <Link
                      key={s}
                      to={`/${s}`}
                      className="inline-flex items-center gap-2 rounded-md border border-sovereign-line bg-white px-3 py-2 text-sm text-ink shadow-card hover:border-brand-purple hover:text-brand-purple"
                    >
                      <span aria-hidden className="text-brand-purple">
                        {t.glyph}
                      </span>
                      <span>{t.title}</span>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </article>
      </main>

      <TabSearchStrip
        slug={tab.slug}
        topic={tab.title}
        {...(tab.searchChips ? { chips: tab.searchChips } : {})}
      />
      <Footer />
    </div>
  );
}
